import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import {
  getComputedStatusColor,
  getComputedStatusLabel,
} from '../../../utils/attendanceDisplay';
import { POLICY_HINTS } from '../../../utils/attendancePolicy';

type Props = {
  status?: string | null;
  size?: 'small' | 'medium';
  withHint?: boolean;
};

/**
 * The single status chip for engine-computed attendance statuses
 * (Present / Late Check-In / Half Day / Absent / Leave / Holiday / Weekly Off).
 * Renders "—" when no status is available (old backend responses).
 */
export const AttendanceStatusChip: React.FC<Props> = ({
  status,
  size = 'small',
  withHint = false,
}) => {
  if (!status || status === 'FUTURE') {
    return (
      <Typography variant="caption" sx={{ color: '#6b7280' }}>
        —
      </Typography>
    );
  }

  const color = getComputedStatusColor(status);
  const label = getComputedStatusLabel(status);

  const chip = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: size === 'small' ? 1.25 : 1.5,
        py: size === 'small' ? 0.35 : 0.5,
        borderRadius: '6px',
        fontSize: size === 'small' ? '0.72rem' : '0.8rem',
        fontWeight: 600,
        color,
        border: `1px solid ${color}55`,
        backgroundColor: `${color}18`,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );

  const hint = withHint ? POLICY_HINTS[status] : undefined;
  if (hint) {
    return (
      <Tooltip title={hint} arrow>
        {chip}
      </Tooltip>
    );
  }
  return chip;
};

export default AttendanceStatusChip;
